import type { ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@gto/ui/cn'
import { NumberInput } from '@gto/ui/number-input'
import { Typography } from '@gto/ui/typography'

import { IconEdgeCoin } from '@/assets/icons'

type RedeemAmountFieldProps = {
  value: number
  onValueChange: (value: number) => void
  isInvalid: boolean
  /** Always visible below the field; announced and shown in red when invalid. */
  helper: ReactNode
}

// Amount inputs on the redeem forms deliberately skip NumberInput min/max:
// Base UI clamps typed values, which would make the insufficient-balance
// error state unreachable. Range enforcement stays on the submit button.
export const RedeemAmountField = ({
  value,
  onValueChange,
  isInvalid,
  helper,
}: RedeemAmountFieldProps) => {
  const helperId = useId()

  return (
    <div className="relative flex-[1_0_auto]">
      <NumberInput
        aria-describedby={helperId}
        aria-invalid={isInvalid || undefined}
        endAddon={<IconEdgeCoin className="size-8" />}
        format={{ useGrouping: false }}
        size="medium"
        value={value}
        onValueChange={(newValue) => {
          onValueChange(newValue ?? 0)
        }}
      />
      <Typography
        component="p"
        id={helperId}
        role={isInvalid ? 'alert' : undefined}
        variant="input/helper"
        className={cn(
          'absolute top-full px-2 text-[12px] text-text-secondary',
          isInvalid && 'text-error-main',
        )}
      >
        {helper}
      </Typography>
    </div>
  )
}
